import React from "react";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ReactComponent as Dropdown } from "../../../../../assets/images/dropdown-arrow.svg";
import { ReactComponent as Cancel } from "../../../../../assets/images/TicketImages/cancel.svg";
import { CUSTOMER_EDIT_STATUS_OPTIONS, CUSTOMER_STATUS_OPTIONS } from "../../../../../constants/peopleData";
import {
  fieldLabelSx,
  inputSx,
  selectSx,
  peopleSelectMenuProps,
  peopleSelectSx,
  TEAM_FORM_WIDTH,
} from "../../Teams/teamFormStyles";
import { CustomerFormFieldSkeleton } from "../CustomerLoadingSkeletons";
import CustomerEditPhoto from "./CustomerEditPhoto";
import TicketTagInput from "../../../TicketSectionComponents/CreateTicket/TicketTagInput";
import { DROPDOWN_HEIGHT } from "../customersConfig";
import { customerEditSelectMenuProps } from "../customersFilterUiShared";

function SelectionChip({ label, icon: Icon, onRemove }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.5,
        py: 0.5,
        borderRadius: "20px",
        border: "1px solid #C5CED6",
        background: "#F9FAFA",
        boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
        height: '32px',
      }}
    >
      {Icon && <Icon style={{ width: 16, height: 16, flexShrink: 0 }} />}
      <Typography sx={{ fontSize: 14, color: "#405261", fontFamily: "Inter" }}>{label}</Typography>
      <Box
        component="button"
        type="button"
        onClick={onRemove}
        sx={{ border: "none", background: "transparent", p: 0, display: "flex", cursor: "pointer" }}
      >
        <Cancel style={{ width: 16, height: 16 ,marginTop: '2px'}} />
      </Box>
    </Box>
  );
}

export default function CustomerEditForm({
  form,
  onChange,
  groupOptions = [],
  optionsLoading = false,
  canEditStatus = false,
}) {
  const set = (key) => (e) => onChange({ ...form, [key]: e.target.value });

  const effectiveGroupOptions = (() => {
    const missing = (form.groupIds || [])
      .filter((id) => !groupOptions.some((g) => g.id === id))
      .map((id) => ({
        id,
        label: form.groupNames?.[id] || id,
      }))
      .filter((g) => g.label);
    return missing.length ? [...groupOptions, ...missing] : groupOptions;
  })();

  const groupLabel = (id) =>
    effectiveGroupOptions.find((g) => g.id === id)?.label ||
    form.groupNames?.[id] ||
    id;

  const handleGroupSelect = (e) => {
    const value = e.target.value;
    if (!value || (form.groupIds || []).includes(value)) return;
    const label = groupOptions.find((g) => g.id === value)?.label || "";
    onChange({
      ...form,
      groupIds: [...(form.groupIds || []), value],
      groupNames: { ...(form.groupNames || {}), ...(label ? { [value]: label } : {}) },
      groupSelect: "",
    });
  };

  const handlePhotoFile = (file) => {
    const preview = URL.createObjectURL(file);
    onChange({ ...form, avatarFile: file, avatarPreview: preview });
  };

  const avatarSrc = form.avatarPreview || form.avatar;
  const statusEditable =
    canEditStatus && (form.status === "active" || form.status === "inactive");
  const statusOptions = statusEditable ? CUSTOMER_EDIT_STATUS_OPTIONS : CUSTOMER_STATUS_OPTIONS;
  const statusLabel =
    CUSTOMER_STATUS_OPTIONS.find((opt) => opt.value === form.status)?.label || form.status;
  const selectMenuProps = customerEditSelectMenuProps();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <CustomerEditPhoto name={form.name} avatarSrc={avatarSrc} onFileSelect={handlePhotoFile} />

      <Box>
        <Typography sx={fieldLabelSx}>Name</Typography>
        <TextField
          fullWidth
          placeholder="Enter your name"
          value={form.name}
          onChange={set("name")}
          sx={inputSx}
        />
      </Box>

      <Box>
        <Typography sx={fieldLabelSx}>Email</Typography>
        <TextField
          fullWidth
          placeholder="Enter your email address"
          value={form.email}
          onChange={set("email")}
          sx={inputSx}
        />
      </Box>

      <Box>
        <Typography sx={fieldLabelSx}>Phone</Typography>
        <TextField
          fullWidth
          placeholder="Enter your phone number"
          value={form.phone}
          onChange={set("phone")}
          sx={inputSx}
        />
      </Box>

      <Box>
        <Typography sx={fieldLabelSx}>Company</Typography>
        <TextField
          fullWidth
          placeholder="Enter your company name"
          value={form.company}
          onChange={set("company")}
          sx={inputSx}
        />
      </Box>

      <Box>
        <Typography sx={fieldLabelSx}>Groups</Typography>
        {optionsLoading ? (
          <CustomerFormFieldSkeleton />
        ) : (
          <>
            <FormControl size="small" sx={selectSx}>
              <Select
                displayEmpty
                value={form.groupSelect}
                onChange={handleGroupSelect}
                IconComponent={Dropdown}
                MenuProps={selectMenuProps}
                sx={peopleSelectSx}
                renderValue={(v) => (
                  <Typography component="span" sx={{ color: v ? "#061522" : "#C5CED6", fontSize: 14 }}>
                    {v ? groupLabel(v) : "Select Group"}
                  </Typography>
                )}
              >
                {effectiveGroupOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id} sx={{ fontFamily: "Inter", fontSize: 14 }}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {(form.groupIds || []).length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                {(form.groupIds || []).map((id) => (
                  <SelectionChip
                    key={id}
                    label={groupLabel(id)}
                    onRemove={() =>
                      onChange({
                        ...form,
                        groupIds: form.groupIds.filter((gid) => gid !== id),
                      })
                    }
                  />
                ))}
              </Box>
            )}
          </>
        )}
      </Box>

      <Box>
        <Typography sx={fieldLabelSx}>Status</Typography>
        <FormControl size="small" sx={selectSx}>
          <Select
            value={form.status}
            onChange={set("status")}
            IconComponent={Dropdown}
            disabled={!statusEditable}
            MenuProps={peopleSelectMenuProps}
            sx={peopleSelectSx}
            renderValue={() => (
              <Typography
                component="span"
                sx={{
                  color: statusEditable ? "#061522" : "#405261",
                  fontSize: 14,
                }}
              >
                {statusLabel}
              </Typography>
            )}
          >
            {statusOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily: "Inter", fontSize: 14 }}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ width: TEAM_FORM_WIDTH, maxWidth: "100%" }}>
        <Typography sx={fieldLabelSx}>Tags</Typography>
        <TicketTagInput
          tags={form.tags}
          setTags={(tags) => onChange({ ...form, tags })}
          showHeader={false}
          showTagsPrefix={false}
          hideInlineChips
          showSelectionTick
          emptyLabel="Search Tags"
          sxHeight={40}
          centerContent
          dropDownTop="calc(100% + 8px)"
          dropdownHeight={DROPDOWN_HEIGHT}
          dropdownMatchTrigger
          formControlSx={{
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        />
        {(form.tags || []).length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
            {(form.tags || []).map((tag) => (
              <SelectionChip
                key={tag}
                label={tag}
                onRemove={() =>
                  onChange({
                    ...form,
                    tags: form.tags.filter((t) => t !== tag),
                  })
                }
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
